import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Check, Sun, Moon } from "lucide-react";
import logoUrl from "@/icons/nelgates-logo-horizontal.png";
import { useTheme } from "@/context/ThemeContext";
import { FloatingIcons, useLoopingStep } from "@/components/auth/AuthShowcase";
import { MailVisual } from "@/components/auth/AuthIllustrations";
import { cn } from "@/lib/utils";

/** Company logo, top left of every sign-in screen */
export function AuthBrand() {
  return <img src={logoUrl} alt="Nelgates Vision Vault" className="h-9 w-auto block" />;
}

function ThemeToggle() {
  const { resolvedTheme, toggleTheme } = useTheme();
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
      className="size-9 rounded-lg border border-border bg-card text-foreground flex items-center justify-center hover:bg-muted"
    >
      {resolvedTheme === "dark" ? <Sun className="size-4 text-warning" /> : <Moon className="size-4" />}
    </button>
  );
}

/**
 * Split screen: the form on the left and a navy brand panel on the right (below the form on phones).
 * Used by every sign-in, invite and password screen.
 */
export function AuthSplit({
  aside,
  topRight,
  floating = false,
  children,
}: {
  aside: React.ReactNode;
  topRight?: React.ReactNode;
  /** Drifting icons around the panel; only for screens whose panel is the browser-window tour */
  floating?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-background">
      <main className="lg:flex-1 flex flex-col px-6 py-6 lg:px-12">
        <header className="flex items-center justify-between gap-3">
          <AuthBrand />
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {topRight}
            <ThemeToggle />
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center py-12">{children}</div>
        <AuthFooter />
      </main>
      <section
        aria-label="About Nelgates Sales"
        className="lg:flex-1 m-3 rounded-[20px] bg-[#07075E] text-white p-8 lg:p-14 flex flex-col justify-between gap-12 overflow-hidden relative"
      >
        <svg
          aria-hidden="true"
          width="640"
          height="640"
          viewBox="0 0 640 640"
          fill="none"
          stroke="#FFFFFF"
          strokeOpacity="0.07"
          className="absolute -right-56 -bottom-56 pointer-events-none"
        >
          <circle cx="320" cy="320" r="120" />
          <circle cx="320" cy="320" r="180" />
          <circle cx="320" cy="320" r="240" />
          <circle cx="320" cy="320" r="300" />
        </svg>
        {floating && <FloatingIcons />}
        <div className="relative flex flex-col gap-12 flex-1">{aside}</div>
      </section>
    </div>
  );
}

function AuthFooter() {
  return (
    <div className="flex justify-end text-xs text-muted-foreground">
      &copy; {new Date().getFullYear()} Nelgates. All rights reserved.
    </div>
  );
}

const RECOVERY_STEPS = ["Enter your work email", "Open the link we email you", "Choose a new password and sign in"];

/** Brand panel with a short explanation and numbered steps that light up one after another, or follow the page */
export function StepsAside({
  eyebrow,
  title,
  text,
  steps,
  visual,
  current,
}: {
  eyebrow: string;
  title: string;
  text: string;
  steps: string[];
  /** Motion graphic under the steps, told which step is lit */
  visual?: (step: number) => React.ReactNode;
  /** The step the person is really on; without it the steps light up in turn on a timer */
  current?: number;
}) {
  // A single step never moves on, so the timer is idle while the page sets the step itself
  const [looped] = useLoopingStep(current === undefined ? steps.length : 1, 2400);
  const step = current ?? looped;
  return (
    <>
      <div className="space-y-4 max-w-[480px]">
        <p className="text-xs font-medium tracking-[0.14em] uppercase text-[#A9B0E8]">{eyebrow}</p>
        <h2 className="text-4xl font-semibold tracking-tight leading-[1.12]">{title}</h2>
        <p className="text-[15.5px] text-[#C3C8F0] leading-relaxed">{text}</p>
      </div>
      <ol className="max-w-[480px]">
        {steps.map((label, i) => (
          <li key={label} className="relative flex items-center gap-3.5 pb-7 last:pb-0 text-[15px]">
            {i < steps.length - 1 && (
              <span className="absolute left-4 top-9 bottom-1 w-px -translate-x-1/2 bg-white/20 overflow-hidden">
                <span
                  className={cn(
                    "block size-full bg-white origin-top transition-transform duration-500 ease-out",
                    i < step ? "scale-y-100" : "scale-y-0",
                  )}
                />
              </span>
            )}
            <span
              className={cn(
                "size-8 shrink-0 rounded-full flex items-center justify-center text-[13px] font-semibold transition-all duration-500",
                i === step && "bg-white text-[#07075E] scale-110 shadow-lg shadow-black/30",
                i < step && "bg-white/15 text-white",
                i > step && "border border-white/30 text-white",
              )}
            >
              {i < step ? <Check className="size-4" /> : i + 1}
            </span>
            <span className={cn("transition-colors duration-500", i === step ? "text-white font-medium" : "text-[#C3C8F0]")}>{label}</span>
          </li>
        ))}
      </ol>
      {visual && <div className="hidden lg:flex flex-1 items-center justify-center">{visual(step)}</div>}
    </>
  );
}

/** Brand panel for the password recovery and expired-link screens */
export function RecoveryAside() {
  return (
    <StepsAside
      eyebrow="Account security"
      title="Back into your account in a few steps."
      text="Reset links are sent only to the email on your account. Each one works once and expires shortly after."
      steps={RECOVERY_STEPS}
      visual={(step) => <MailVisual step={step} />}
    />
  );
}

/** Centred form on the left and the recovery brand panel on the right, for the short recovery screens */
export function AuthCardPage({
  aside,
  topRight,
  children,
}: {
  aside?: React.ReactNode;
  topRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <AuthSplit aside={aside ?? <RecoveryAside />} topRight={topRight}>
      <div className="w-full max-w-[400px]">{children}</div>
    </AuthSplit>
  );
}

/** The form block itself: heading, an optional status label above it, then the fields */
export function AuthCard({
  status,
  tone = "primary",
  title,
  subtitle,
  children,
  className,
}: {
  /** Short label above the heading for screens that report a state (locked, expired, sent) */
  status?: string;
  tone?: "primary" | "success" | "danger" | "warning";
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  const tones = {
    primary: "bg-primary/10 text-primary",
    success: "bg-success-soft text-success",
    danger: "bg-danger-soft text-danger",
    warning: "bg-warning-soft text-warning",
  };
  return (
    <section className={cn("w-full flex flex-col gap-5", className)}>
      {status && (
        <span className={cn("self-start inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium", tones[tone])}>
          <span className="size-1.5 rounded-full bg-current" />
          {status}
        </span>
      )}
      <div className="space-y-1.5">
        <h1 className="text-[28px] font-semibold tracking-tight leading-[1.2] text-foreground">{title}</h1>
        {subtitle && <p className="text-[15px] text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

/** Input shell with a leading icon; the focus ring is drawn around the whole row */
export function AuthField({
  id,
  label,
  icon,
  right,
  hint,
  tone,
  children,
  labelRight,
}: {
  id: string;
  label: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  hint?: React.ReactNode;
  tone?: "ok" | "bad";
  children: React.ReactNode;
  labelRight?: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-[13px] font-medium text-foreground">
          {label}
        </label>
        {labelRight}
      </div>
      <div
        className={cn(
          "flex items-center gap-2.5 h-[46px] rounded-lg border bg-background pl-3.5 pr-1.5 transition-shadow",
          "focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/15",
          tone === "ok" && "border-success focus-within:border-success focus-within:ring-success/15",
          tone === "bad" && "border-danger focus-within:border-danger focus-within:ring-danger/15",
          !tone && "border-input",
        )}
      >
        {icon && <span className="text-muted-foreground [&>svg]:size-[17px]">{icon}</span>}
        {children}
        {right}
      </div>
      {hint}
    </div>
  );
}

export const authInputClass = "flex-1 min-w-0 bg-transparent outline-none text-[15px] text-foreground placeholder:text-muted-foreground";

/** Password input with a show/hide button */
export function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
  autoFocus,
  maxLength = 72,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  autoFocus?: boolean;
  maxLength?: number;
  placeholder?: string;
}) {
  const [shown, setShown] = useState(false);
  return (
    <>
      <input
        id={id}
        type={shown ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        maxLength={maxLength}
        placeholder={placeholder}
        className={authInputClass}
        required
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? "Hide password" : "Show password"}
        className="size-10 rounded-md text-muted-foreground hover:bg-muted flex items-center justify-center"
      >
        {shown ? <EyeOff className="size-[17px]" /> : <Eye className="size-[17px]" />}
      </button>
    </>
  );
}

export function passwordRules(password: string, minLength: number) {
  return [
    { ok: password.length >= minLength, label: `At least ${minLength} characters` },
    { ok: /\d/.test(password), label: "A number" },
    { ok: /[a-z]/.test(password) && /[A-Z]/.test(password), label: "Upper and lower case" },
    { ok: password.length >= minLength + 4, label: "Longer is stronger" },
  ];
}

/** Four-segment strength bar and a checklist; advisory only, the server decides what is accepted */
export function PasswordStrength({ password, minLength }: { password: string; minLength: number }) {
  const rules = passwordRules(password, minLength);
  const score = password ? rules.filter((r) => r.ok).length : 0;
  const label = ["", "Weak", "Fair", "Good", "Strong"][score];
  const color = score >= 3 ? "bg-success" : score === 2 ? "bg-warning" : "bg-danger";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex-1 grid grid-cols-4 gap-1" role="img" aria-label={`Password strength: ${label || "empty"}`}>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={cn("h-1 rounded-full", i < score ? color : "bg-border")} />
          ))}
        </div>
        {label && <span className={cn("text-xs font-medium", score >= 3 ? "text-success" : "text-muted-foreground")}>{label}</span>}
      </div>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[13px]">
        {rules.map((r) => (
          <li key={r.label} className={cn("flex items-center gap-1.5", r.ok ? "text-success" : "text-muted-foreground")}>
            {r.ok ? <Check className="size-3.5" /> : <span className="size-3.5 rounded-full border border-current opacity-60" />}
            {r.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Brand navy primary button used on every sign-in screen */
export const authPrimaryButton =
  "h-[46px] w-full rounded-lg bg-[#000080] text-white text-[15px] font-medium flex items-center justify-center gap-2 shadow-sm hover:bg-[#00006B] disabled:opacity-60 transition-colors";

export function BackToSignIn() {
  return (
    <Link to="/login" className="text-sm font-medium text-primary hover:underline">
      Back to sign in
    </Link>
  );
}
